/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap-v5';
import { ToastContainer, toast } from 'react-toastify';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';
import GetPagePermission from '../components/GetPagePermission';
import Error from '../components/Error500';
import { KTSVG } from '../../_metronic/helpers';
import { postRequestOptions } from '../helpers/Fetchwrapper';

type ListMode = 'pending' | 'reconciled';

const numberFormat = (value: any) => {
    const amount = parseFloat(value);
    if (Number.isNaN(amount)) {
        return '0.00';
    }

    return amount.toLocaleString('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const voucherTypeOptions = [
    { value: 'payment', label: 'Bank Payment' },
    { value: 'receipt', label: 'Bank Receipt' },
    { value: 'contra', label: 'Bank Contra' },
    { value: 'journal', label: 'Journal' },
];

const toDateInputValue = (value: any): string => {
    if (!value) {
        return '';
    }

    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        return raw.substring(0, 10);
    }

    return '';
};

function Reconciliation() {
    const hasPermission = GetPagePermission('/reconciliation');
    const [listMode, setListMode] = useState<ListMode>('pending');
    const [newData, setNewData] = useState<{ id: number }>({ id: Date.now() });
    const [selectedDates, setSelectedDates] = useState<Record<number, string>>({});
    const [savingIds, setSavingIds] = useState<Record<number, boolean>>({});

    const additionalPostData = useMemo(() => ({ status: listMode }), [listMode]);

    const getSelectedDate = (row: any): string => {
        const id = Number(row.idAccVoucherRows);
        if (selectedDates[id] !== undefined) {
            return selectedDates[id];
        }

        return toDateInputValue(row.voucherDateRaw || row.voucherDate);
    };

    const handleDateChange = (idAccVoucherRows: number, value: string) => {
        setSelectedDates((prev) => ({
            ...prev,
            [idAccVoucherRows]: value,
        }));
    };

    const refreshList = () => setNewData({ id: Date.now() });

    const switchTab = (mode: ListMode) => {
        if (mode === listMode) {
            return;
        }
        setListMode(mode);
        setSelectedDates({});
        setNewData({ id: Date.now() });
    };

    const saveReconciliation = async (row: any) => {
        const id = Number(row.idAccVoucherRows);
        const reconciliationDate = getSelectedDate(row);

        if (!reconciliationDate) {
            toast.error('Please select a reconciliation date.');
            return;
        }

        setSavingIds((prev) => ({ ...prev, [id]: true }));

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}reconciliation/update-date`,
                postRequestOptions({
                    idAccVoucherRows: id,
                    reconciliationDate,
                    action: 'save',
                })
            );
            const result = await response.json();

            if (!response.ok || !result.success) {
                const errorMessage =
                    result.errorMessage ||
                    Object.values(result.errors || {}).flat().join(', ') ||
                    'Failed to save reconciliation';
                toast.error(String(errorMessage));
                return;
            }

            toast.success(result.successMessage || 'Reconciliation saved successfully.');
            setSelectedDates((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            refreshList();
        } catch (error: any) {
            toast.error(error?.message || 'Request failed');
        } finally {
            setSavingIds((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        }
    };

    const undoReconciliation = (row: any) => {
        const id = Number(row.idAccVoucherRows);

        Swal.fire({
            title: 'Undo reconciliation?',
            html: `<div class="text-start">
                <div><strong>Voucher:</strong> ${row.voucherNo || ''}</div>
                <div><strong>Ledger:</strong> ${row.ledger || ''}</div>
                <div class="mt-2 text-muted">This entry will move back to the pending list.</div>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, undo',
            cancelButtonText: 'Cancel',
            showLoaderOnConfirm: true,
            customClass: {
                confirmButton: 'btn btn-warning me-2',
                cancelButton: 'btn btn-secondary',
            },
            preConfirm: async () => {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}reconciliation/update-date`,
                        postRequestOptions({
                            idAccVoucherRows: id,
                            reconciliationDate: null,
                            action: 'undo',
                        })
                    );
                    const result = await response.json();
                    if (!response.ok || !result.success) {
                        throw new Error(
                            result.errorMessage ||
                            Object.values(result.errors || {}).flat().join(', ') ||
                            'Failed to undo reconciliation'
                        );
                    }
                    return result;
                } catch (error: any) {
                    Swal.showValidationMessage(error?.message || 'Request failed');
                    return null;
                }
            },
        }).then((result) => {
            if (result.isConfirmed && result.value?.success) {
                toast.success(result.value.successMessage || 'Reconciliation undone.');
                refreshList();
            }
        });
    };

    const sharedColumns = [
        {
            value: 'id',
            title: '#',
            style: { textAlign: 'center', width: '60px' },
        },
        {
            value: 'voucherDate',
            title: 'Date',
            style: { textAlign: 'left', whiteSpace: 'nowrap' },
            filterType: 'date',
        },
        {
            value: 'voucherNo',
            title: 'Voucher No',
            style: { textAlign: 'left', whiteSpace: 'nowrap' },
            hasActionsComponent: true,
            actionsComponent: (row: any) => (
                <Link
                    to={`/transaction-details/${row.idAccVouchers}`}
                    className="text-primary fw-semibold text-hover-primary"
                >
                    {row.voucherNo}
                </Link>
            ),
        },
        {
            value: 'chequeNo',
            title: 'Cheque No',
            style: { textAlign: 'left' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => (
                <span className="text-muted">{row.chequeNo || '—'}</span>
            ),
        },
        {
            value: 'ledger',
            title: 'Ledger',
            style: { textAlign: 'left' },
        },
        {
            value: 'voucherType',
            title: 'Type',
            style: { textAlign: 'left', whiteSpace: 'nowrap' },
            filterType: 'select',
            filterPlaceholder: 'Select Voucher Type',
            filterOptions: voucherTypeOptions,
            hasHtmlComponent: true,
            htmlComponent: (row: any) => {
                const badgeClass =
                    row.voucherTypeRaw === 'payment'
                        ? 'badge-light-danger'
                        : row.voucherTypeRaw === 'receipt'
                            ? 'badge-light-success'
                            : 'badge-light-primary';

                return <span className={`badge ${badgeClass}`}>{row.voucherType}</span>;
            },
        },
        {
            value: 'drAmount',
            title: 'Debit Amount',
            style: { textAlign: 'right', whiteSpace: 'nowrap' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => (
                <span className="fw-semibold">{numberFormat(row.drAmount)}</span>
            ),
        },
        {
            value: 'crAmount',
            title: 'Credit Amount',
            style: { textAlign: 'right', whiteSpace: 'nowrap' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => (
                <span className="fw-semibold">{numberFormat(row.crAmount)}</span>
            ),
        },
    ];

    const pendingColumns = [
        ...sharedColumns,
        {
            value: 'reconciliationDate',
            title: 'Reconciliation Date',
            style: { textAlign: 'left', minWidth: '160px' },
            hideFilter: true,
            hasActionsComponent: true,
            actionsComponent: (row: any) => {
                const id = Number(row.idAccVoucherRows);
                const isSaving = !!savingIds[id];

                return (
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '150px' }}
                        value={getSelectedDate(row)}
                        disabled={isSaving}
                        onChange={(e) => handleDateChange(id, e.target.value)}
                    />
                );
            },
        },
        {
            value: 'action',
            title: 'Action',
            style: { textAlign: 'center', whiteSpace: 'nowrap', minWidth: '170px' },
            hasActionsComponent: true,
            actionsComponent: (row: any) => {
                const id = Number(row.idAccVoucherRows);
                const isSaving = !!savingIds[id];

                return (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={isSaving || !getSelectedDate(row)}
                        onClick={() => saveReconciliation(row)}
                    >
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check me-1"></i>
                                Save Reconciliation
                            </>
                        )}
                    </button>
                );
            },
        },
    ];

    const reconciledColumns = [
        ...sharedColumns,
        {
            value: 'reconciliationDate',
            title: 'Reconciliation Date',
            style: { textAlign: 'left', whiteSpace: 'nowrap' },
            filterType: 'date',
            hasHtmlComponent: true,
            htmlComponent: (row: any) => (
                <span className="badge badge-light-success fs-7">
                    {row.reconciliationDateFormatted || row.reconciliationDate || '—'}
                </span>
            ),
        },
        {
            value: 'action',
            title: 'Action',
            style: { textAlign: 'center', whiteSpace: 'nowrap', minWidth: '160px' },
            hasActionsComponent: true,
            actionsComponent: (row: any) => (
                <button
                    type="button"
                    className="btn btn-sm btn-light-warning"
                    onClick={() => undoReconciliation(row)}
                >
                    <i className="fas fa-undo me-1"></i>
                    Undo
                </button>
            ),
        },
    ];

    const columns = listMode === 'pending' ? pendingColumns : reconciledColumns;

    const filters: any = {};
    columns.forEach((column) => {
        filters[column.value] = {
            filterValue: '',
            filterType: column.filterType === 'select' || column.filterType === 'date' ? '=' : 'like',
        };
    });

    const url = `${process.env.REACT_APP_API_URL}reconciliation`;

    return (
        <>
            {hasPermission === 1 && (
                <div className="card card-custom">
                    <div className="card-header card-header-stretch overflow-auto">
                        <div className="card-title">
                            <KTSVG
                                path="/media/svg/icons/Shopping/Chart-bar1.svg"
                                className="svg-icon-1 svg-icon-primary"
                            />
                            <span className="text fw-bolder" style={{ marginLeft: '10px' }}>
                                Reconciliation
                            </span>
                            <small className="text-muted ms-2">
                                {listMode === 'pending'
                                    ? 'Pending bank ledger entries awaiting reconciliation'
                                    : 'Already reconciled bank ledger entries'}
                            </small>
                        </div>
                        <div className="card-toolbar">
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary"
                                onClick={refreshList}
                            >
                                <i className="fas fa-sync-alt me-1"></i>
                                Refresh
                            </button>
                        </div>
                    </div>
                    <div className="card-body pt-3">
                        <Tabs
                            activeKey={listMode}
                            onSelect={(key) => switchTab((key as ListMode) || 'pending')}
                            className="nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold mb-5"
                        >
                            <Tab
                                eventKey="pending"
                                title={
                                    <span className="d-flex align-items-center">
                                        <KTSVG
                                            path="/media/svg/icons/Code/Time-schedule.svg"
                                            className="svg-icon-4 me-2"
                                        />
                                        Pending
                                    </span>
                                }
                            />
                            <Tab
                                eventKey="reconciled"
                                title={
                                    <span className="d-flex align-items-center">
                                        <KTSVG
                                            path="/media/svg/icons/Navigation/Check.svg"
                                            className="svg-icon-4 me-2"
                                        />
                                        Reconciliation List
                                    </span>
                                }
                            />
                        </Tabs>

                        <div
                            className={`notice d-flex rounded border border-dashed p-4 mb-5 ${
                                listMode === 'pending'
                                    ? 'bg-light-primary border-primary'
                                    : 'bg-light-success border-success'
                            }`}
                        >
                            <KTSVG
                                path="/media/svg/icons/Code/Info-circle.svg"
                                className={`svg-icon-2tx me-4 ${
                                    listMode === 'pending' ? 'svg-icon-primary' : 'svg-icon-success'
                                }`}
                            />
                            <div className="d-flex flex-stack flex-grow-1">
                                <div className="fw-semibold">
                                    <div className="fs-6 text-gray-700">
                                        {listMode === 'pending' ? (
                                            <>
                                                Select a reconciliation date (defaults to voucher date), then click
                                                <span className="fw-bold"> Save Reconciliation</span>.
                                                Saved entries move to the <span className="fw-bold">Reconciliation List</span> tab.
                                            </>
                                        ) : (
                                            <>
                                                These entries are already reconciled.
                                                Use <span className="fw-bold">Undo</span> if a row was reconciled by mistake —
                                                it will return to the Pending tab.
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <DataTable
                            key={listMode}
                            url={url}
                            columns={columns}
                            filters={filters}
                            newData={newData}
                            additionalPostData={additionalPostData}
                            defaultSortedColumn={listMode === 'reconciled' ? 'reconciliationDate' : 'voucherDate'}
                            defaultSortOrder="desc"
                            defaultPerPage={20}
                        />
                    </div>
                </div>
            )}
            {hasPermission === 0 && <Error />}
            <ToastContainer />
        </>
    );
}

export default Reconciliation;
